import { useState, useEffect, useCallback } from 'react';
import {
  getVideoMessages,
  markVideoMessageDone,
  markVideoMessageWatched,
  markVideoMessageOpened,
} from '../api/videoMessages';
import { reportUserFacingError } from '../../../utils/errorReporting';

/**
 * Loads the video messages waiting for a student and marks them done.
 *
 * Teacher view is read-only: a teacher previewing a kid's dashboard should see
 * the card that kid sees, but pressing done on their behalf would silently
 * consume the message, so `markDone` is never wired up there.
 *
 * @param {string} studentId
 * @param {boolean} isSignedIn
 * @param {Object} user
 * @returns {{
 *   messages: Array,
 *   activeMessage: Object | null,
 *   openMessage: (message: Object) => void,
 *   closeMessage: () => void,
 *   markOpened: (messageId: number) => void,
 *   markFinished: (messageId: number) => void,
 *   markDone: (messageId: number) => Promise<void>,
 *   markingDone: boolean
 * }}
 */
export function useVideoMessages(studentId, isSignedIn, user) {
  const [messages, setMessages] = useState([]);
  const [activeMessage, setActiveMessage] = useState(null);
  const [markingDone, setMarkingDone] = useState(false);

  useEffect(() => {
    if (!studentId || !isSignedIn || !user) {
      setMessages([]);
      return undefined;
    }

    let cancelled = false;

    getVideoMessages(studentId)
      .then(data => {
        if (cancelled) return;
        setMessages(data?.messages || []);
      })
      .catch(error => {
        if (cancelled) return;
        // A failed fetch must not block the dashboard — the kid simply does
        // not see the card this load, and it reappears on the next one.
        reportUserFacingError(error, { context: 'Load video messages' });
        setMessages([]);
      });

    return () => { cancelled = true; };
  }, [studentId, isSignedIn, user]);

  const openMessage = useCallback((message) => setActiveMessage(message), []);
  const closeMessage = useCallback(() => setActiveMessage(null), []);

  /**
   * Engagement pings. Both are fire-and-forget and must never block the kid or
   * put a spinner on screen: they exist purely so the teacher can see whether
   * messages are being watched. A dropped ping costs one data point, which is
   * a far better trade than interrupting a child mid-video.
   */
  const markOpened = useCallback((messageId) => {
    markVideoMessageOpened(studentId, messageId).catch(error => {
      if (error?.statusCode !== 404) {
        reportUserFacingError(error, { context: 'Mark video message opened' });
      }
    });
  }, [studentId]);

  const markFinished = useCallback((messageId) => {
    // Local watched_at flips the card to "watch again" without waiting on the
    // round trip.
    setMessages(prev => prev.map(m => (
      m.id === messageId && !m.watched_at
        ? { ...m, watched_at: new Date().toISOString() }
        : m
    )));
    markVideoMessageWatched(studentId, messageId).catch(error => {
      if (error?.statusCode !== 404) {
        reportUserFacingError(error, { context: 'Mark video message watched' });
      }
    });
  }, [studentId]);

  const markDone = useCallback(async (messageId) => {
    setMarkingDone(true);
    try {
      await markVideoMessageDone(studentId, messageId);
    } catch (error) {
      // 404 means it was already done (double-tap, or another tab got there
      // first). The card should disappear either way, so only real failures
      // are worth reporting.
      if (error?.statusCode !== 404) {
        reportUserFacingError(error, { context: 'Mark video message done' });
      }
    } finally {
      setMarkingDone(false);
      // Drop it locally regardless: leaving a card the server has already
      // retired would strand the kid on a message they cannot dismiss.
      setMessages(prev => prev.filter(m => m.id !== messageId));
      setActiveMessage(null);
    }
  }, [studentId]);

  return {
    messages, activeMessage, openMessage, closeMessage,
    markOpened, markFinished, markDone, markingDone,
  };
}
